import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { toast } from "./use-toast";

export const useOrganizationInitializer = () => {
  const [isCreating, setIsCreating] = useState(false);
  const { user, profile } = useAuth();

  const createOrganization = async (organizationName?: string) => {
    if (!user || !profile) {
      toast({
        title: "Erreur",
        description: "Vous devez être connecté pour créer une organisation",
        variant: "destructive",
      });
      return { error: "User not authenticated" };
    }

    setIsCreating(true);

    try {
      // Utiliser le nom de l'entreprise du profil ou le nom fourni
      const orgName = organizationName || profile.company || "Mon Organisation";

      // Créer l'organisation
      const { data: orgData, error: orgError } = await supabase
        .from("organizations")
        .insert({
          name: orgName,
          created_by: user.id,
        })
        .select()
        .single();

      if (orgError) {
        console.error("Error creating organization:", orgError);
        toast({
          title: "Erreur",
          description: "Impossible de créer l'organisation",
          variant: "destructive",
        });
        return { error: orgError };
      }

      // Ajouter l'utilisateur comme propriétaire
      const { error: memberError } = await supabase
        .from("organization_members")
        .insert({
          organization_id: orgData.id,
          user_id: user.id,
          role: "owner",
          status: "active",
          activated_at: new Date().toISOString(),
        });

      if (memberError) {
        console.error("Error adding user as member:", memberError);
        toast({
          title: "Erreur",
          description: "Impossible d'ajouter l'utilisateur à l'organisation",
          variant: "destructive",
        });
        return { error: memberError };
      }

      // Mettre à jour le profil avec l'ID de l'organisation
      const { error: profileError } = await supabase
        .from("profiles")
        .update({ organization_id: orgData.id })
        .eq("id", user.id);

      if (profileError) {
        console.error("Error updating profile:", profileError);
        // Non critique, on continue
      }

      // Créer une subscription par défaut si la table existe
      try {
        await supabase
          .from("recruiter_credits")
          .insert({
            user_id: user.id,
            credits_total: 100,
            credits_remaining: 100,
          });
      } catch (subError) {
        console.error("Error creating user credits:", subError);
        // Non critique, on continue
      }

      toast({
        title: "Organisation créée",
        description: `L'organisation "${orgName}" a été créée avec succès`,
      });

      return { error: null, organizationId: orgData.id };
    } catch (_error) {
      console.error("Unexpected error:", _error);
      toast({
        title: "Erreur",
        description: "Une erreur inattendue s'est produite",
        variant: "destructive",
      });
      return { error };
    } finally {
      setIsCreating(false);
    }
  };

  return {
    createOrganization,
    isCreating,
  };
};